import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { Node } from '@xyflow/react';
import React from 'react';
import { Accordion } from 'src/components/accordion';
import { twMerge } from 'tailwind-merge';

import { IpInterface, NodeCategoryModel } from '../../models/node-data';
import WizardIpRow from './wizard-ip-row';

interface WizardAccordionNodeProps {
  title: string;
  allVariables: string[];
  nodes: Node[];
  useCustomVlan: boolean;
  handleUpdateInterface: (
    nodeId: string,
    interfaceIndex: number,
    key: string,
    value: string,
  ) => void;
  handleUpdateNode: (nodeId: string, key: string, value: string) => void;
}

export default function WizardAccordionNode({
  title,
  allVariables,
  nodes,
  useCustomVlan,
  handleUpdateInterface,
  handleUpdateNode,
}: WizardAccordionNodeProps) {
  return (
    <Accordion
      title={title}
      subtitle={allVariables.length > 0 ? `(${allVariables.join(',')})` : ''}
      panelClassName="p-0"
      action={
        allVariables.length > 0 && (
          <CheckCircleIcon className="size-6 text-green-500" />
        )
      }
    >
      <table className="w-full divide-y divide-gray-200 rounded-b-md border border-gray-200 text-base text-gray-900">
        <thead>
          <tr className="bg-gray-100">
            <th className="border-r border-gray-200 p-1 text-center">Name</th>
            {useCustomVlan && (
              <th className="border-r border-gray-200 p-1 text-center">
                Vlan ID
              </th>
            )}
            <th className="border-r border-gray-200 p-1 text-center">IP</th>
            <th className="border-r text-center">Subnet Mask</th>
          </tr>
        </thead>
        <tbody>
          {nodes.map((node) => {
            const categoryData = node.data?.category as NodeCategoryModel;

            const hasVlan =
              categoryData.ipInterfaces && categoryData.ipInterfaces.length > 0;

            const rowSpan = hasVlan ? categoryData.ipInterfaces?.length : 1;
            let firstInterface: IpInterface | undefined;
            let restInterfaces: IpInterface[] | undefined;
            if (hasVlan) {
              firstInterface = categoryData.ipInterfaces?.[0];
              restInterfaces = categoryData.ipInterfaces?.slice(1);
            }

            return (
              <React.Fragment key={node.id}>
                <tr className="border-b border-gray-200 last:border-b-0 even:bg-gray-50/50">
                  <td
                    className={twMerge(['p-1', 'border-r border-gray-200'])}
                    rowSpan={rowSpan}
                  >
                    <span className="block max-w-40 truncate text-base text-gray-900">
                      {categoryData.name || ''}
                    </span>
                  </td>
                  {useCustomVlan ? (
                    <>
                      <td className="border-r border-gray-200 p-1 text-center">
                        <span className="text-center text-base text-gray-900">
                          {firstInterface?.vlanId || ''}
                        </span>
                      </td>
                      {hasVlan ? (
                        <WizardIpRow
                          id={`${node.id}-interface-0`}
                          ip={firstInterface?.ip || ''}
                          subnetMask={firstInterface?.subnetMask || ''}
                          onChangeIp={(value) => {
                            handleUpdateInterface(node.id, 0, 'ip', value);
                          }}
                          onChangeSubnetMask={(value) => {
                            handleUpdateInterface(
                              node.id,
                              0,
                              'subnetMask',
                              value,
                            );
                          }}
                        />
                      ) : (
                        <WizardIpRow
                          id={node.id}
                          ip={categoryData.ip || ''}
                          subnetMask={categoryData.subnetMask || ''}
                          onChangeIp={(value) => {
                            handleUpdateNode(node.id, 'ip', value);
                          }}
                          onChangeSubnetMask={(value) => {
                            handleUpdateNode(node.id, 'subnetMask', value);
                          }}
                        />
                      )}
                    </>
                  ) : (
                    <WizardIpRow
                      id={node.id}
                      ip={categoryData.ip || ''}
                      subnetMask={categoryData.subnetMask || ''}
                      onChangeIp={(value) => {
                        handleUpdateNode(node.id, 'ip', value);
                      }}
                      onChangeSubnetMask={(value) => {
                        handleUpdateNode(node.id, 'subnetMask', value);
                      }}
                    />
                  )}
                </tr>
                {restInterfaces &&
                  restInterfaces.length > 0 &&
                  restInterfaces?.map((interfaceData, i) => (
                    <tr
                      key={i}
                      className="border-b border-gray-200 last:border-b-0 even:bg-gray-50/50"
                    >
                      <td className="border-r border-gray-200 p-1 text-center">
                        <span className="text-base text-gray-900">
                          {interfaceData?.vlanId || ''}
                        </span>
                      </td>
                      <WizardIpRow
                        id={`${node.id}-interface-${i + 1}`}
                        ip={interfaceData?.ip || ''}
                        subnetMask={interfaceData?.subnetMask || ''}
                        onChangeIp={(value) => {
                          handleUpdateInterface(node.id, i + 1, 'ip', value);
                        }}
                        onChangeSubnetMask={(value) => {
                          handleUpdateInterface(
                            node.id,
                            i + 1,
                            'subnetMask',
                            value,
                          );
                        }}
                      />
                    </tr>
                  ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </Accordion>
  );
}
